import * as React from "react";
import { useCallback, useMemo, useState } from "react";
import styled from "@mui/material/styles/styled";
import {
  getPatientFindingsSection,
  panelGroupToCaseGrouping,
  usePatientCase,
} from "../usePatientCase";
import { groupBy } from "fe-common/ui/arrayUtils";
import { getContainedSubGroup } from "../config/dataInputSectionConfiguration";
import { KahunPanels, KahunPanelSubGroup, Relations, Values } from "shared";
import { PatientCaseGrouping } from "../../../context/diagnosticAgent";
import { PatientChartWorkupPanels } from "./PatientChartWorkupPanels";
import { PatientChartSubGroupTitle } from "./PatientChartSubGroupTitle";
import { PatientChartSection } from "./PatientCaseContainer";
import {
  RosebudRouteSetting,
  RosebudRoutesSettings,
} from "../util/rosebudRoutesSettings";
import { PatientChartFinding } from "./PatientChartFinding";
import List from "@mui/material/List";
import { useDiagnosticNavigation } from "../useDiagnosticNavigation";
import { AgentRoute } from "../util/agentRoutes";
import { useScreenSize } from "../util/useScreenSize";
import { useRouteHelper } from "../useRouteHelper";
import { RosebudColorMap } from "../rosebud/rosebudColorMap";
import { MobileView } from "../../../util/aKaunGeneralUtil";
import { useOrganizedPanels } from "../editPatient/useOrganizedPanels";
import { useDataInputPanels } from "../useDataInputPanels";
import { useGetPanelAbnormalQf } from "../hooks/useGetPanelAbnormalQf";
import { sortBy } from "lodash";

//region [[ Styles ]]

const PatientChartGroupedFeaturesView = styled("div")(({ theme }) => ({
  "& .MuiList-padding": {
    paddingBottom: "0px",
  },
}));

const GroupedFeatureList = styled(List)({
  paddingTop: 0,
});

const ShowAllText = styled("div")(({ theme }) => ({
  fontWeight: 500,
  fontSize: "14px",

  [theme.breakpoints.down(MobileView)]: {
    marginRight: "10px",
  },

  color: RosebudColorMap.gray2,
}));

//endregion [[ Styles ]]

//region [[ Props ]]

export interface PatientChartGroupedFeaturesProps {
  section: PatientCaseGrouping;
  step: RosebudRouteSetting;
  onClick: any;
  showAllSubGroups: boolean;
}

//endregion [[ Props ]]

//region [[ Functions ]]

const QfsList = ({ qfs, gotoSection, remove, onRefine, subGroupId }) => {
  const sortedQfs = sortBy(qfs, "negate");

  const getPatientChartFinding = (qf, index) => {
    const handleRefineClicked = (e) => {
      if (!onRefine) return;
      onRefine(e, qf.feature.id);
    };

    const onDelete = (event) => {
      event.stopPropagation();
      remove(qf);
    };

    return (
      <PatientChartFinding
        key={index}
        qf={qf}
        gotoSection={gotoSection}
        index={index}
        isEditable={subGroupId === KahunPanelSubGroup.SignSymptom}
        onRefine={
          subGroupId === KahunPanelSubGroup.SignSymptom ||
          subGroupId === KahunPanelSubGroup.Symptoms
            ? handleRefineClicked
            : null
        }
        onDelete={onDelete}
      />
    );
  };

  return <>{sortedQfs.map((qf, index) => getPatientChartFinding(qf, index))}</>;
};

//endregion [[ Functions ]]

export const PatientChartGroupedFeatures = ({
  ...props
}: PatientChartGroupedFeaturesProps) => {
  const { patientCase, handleChipRemove } = usePatientCase();
  const { isMobile } = useScreenSize();

  const { navigateTo } = useRouteHelper();
  const { panels } = useDataInputPanels();
  const { isAllNormal: isAllVitalSignsNormal } = useGetPanelAbnormalQf(
    PatientCaseGrouping.CLINICAL_FEATURES,
    KahunPanels.VitalSigns,
  );
  const [showMore, setShowMore] = useState(false);

  // filter panels to correct section
  const sectionPanels = useMemo(
    () =>
      [...panels.values()].filter(
        (p) => panelGroupToCaseGrouping(p?.panelGroup?.id) === props.section,
      ),
    [panels, props.section],
  );

  let { panelsBySubGroup } = useOrganizedPanels(
    sectionPanels,
    null,
    props.section,
  );

  const remove = (f) => handleChipRemove(f, props.section);
  const qfs = getPatientFindingsSection(props.section, patientCase).filter(
    (qf) => !qf.skip,
  );
  const qfsGrouped = groupBy(
    qfs,
    (qf) => getContainedSubGroup(qf, props.section).id,
  );
  const { setRefinePopupProps } = useDiagnosticNavigation();
  const dataEntryStep = RosebudRoutesSettings.filter(
    (s) => s.route === AgentRoute.DataEntry,
  ).pop();

  const getFilteredFindings = useCallback(
    (subGroupId: string) => {
      if (subGroupId === KahunPanelSubGroup.VitalSign) {
        const vitalSignsFindings = qfsGrouped[subGroupId];

        const findingsNotNormal = vitalSignsFindings
          ? vitalSignsFindings.filter(
              (finding) =>
                finding.qualifiers.filter(
                  (q) =>
                    q.relation.id === Relations.LEVEL &&
                    q.value.id !== Values.NORMAL,
                ).length > 0,
            )
          : [];

        const findingsNormal = vitalSignsFindings
          ? vitalSignsFindings.filter(
              (finding) =>
                finding.qualifiers.filter(
                  (q) =>
                    q.relation.id === Relations.LEVEL &&
                    q.value.id === Values.NORMAL,
                ).length > 0,
            )
          : [];

        if (showMore) {
          return findingsNormal;
        } else {
          return findingsNotNormal;
        }
      } else {
        return qfsGrouped[subGroupId];
      }
    },
    [qfsGrouped, showMore],
  );

  const subGroups = useMemo(() => {
    return panelsBySubGroup.map((panel) => {
      return {
        id: panel[0].id,
        qfs:
          props.section === PatientCaseGrouping.WORKUP
            ? qfsGrouped[panel[0].id]
            : getFilteredFindings(panel[0].id),
      };
    });
  }, [getFilteredFindings, panelsBySubGroup, qfsGrouped, props.section]).filter(
    (sg) => isMobile || sg.id !== KahunPanelSubGroup.VitalSign,
  );

  const onShowAll = (event) => {
    event.stopPropagation();
    setShowMore(!showMore);
  };

  const handleChartPanelClick = useCallback(
    (
      step: RosebudRouteSetting,
      section?: PatientCaseGrouping,
      subGroupId?: string,
      panelId?: string,
    ) => () => {
      // in case mobile go to "DataEntry" panel
      if (isMobile) {
        props.onClick(step);
        navigateTo({ step: step, grouping: section, subGroupId, panelId });
      }
      // go to specific panel (background, workup etc..)
      else {
        if (props.onClick) {
          props.onClick(step);
        }
        navigateTo({ step, subGroupId, panelId });
      }
    },
    [navigateTo, isMobile, props],
  );

  const handleGoToSection = (subGroupId, panelId) => {
    handleChartPanelClick(props.step, props.section, subGroupId, panelId)();
  };

  const handleOnRefineClicked = (subGroupId) =>
    subGroupId === KahunPanelSubGroup.SignSymptom ||
    subGroupId === KahunPanelSubGroup.Symptoms
      ? (e, qfId) => onRefine(e, qfId, props.section)
      : null;

  const createSubGroupClickHandler = (step) => (section, subGroupId) => {
    if (isMobile) {
      return navigateTo({
        step: dataEntryStep,
        grouping: section,
        subGroupId,
      });
    } else {
      return navigateTo({ step, subGroupId });
    }
  };

  const onRefine = useCallback(
    (e, id, section) => {
      const selectedRefineQf = {
        evt: e.currentTarget,
        id,
        section,
      };

      const qfs = getPatientFindingsSection(section, patientCase).filter(
        (qf) => !qf.skip,
      );

      const refineQf =
        selectedRefineQf && section === selectedRefineQf.section
          ? qfs.find((qf) => qf.feature.id === id)
          : null;

      setRefinePopupProps({
        open: true,
        refineQualifier: refineQf,
        patientCaseGrouping: section,
      });
    },
    [setRefinePopupProps, patientCase],
  );

  return (
    <PatientChartGroupedFeaturesView>
      <GroupedFeatureList>
        {subGroups.map((subGroup: any) => (
          <React.Fragment key={subGroup.id}>
            {(props.showAllSubGroups || subGroup.qfs) && (
              <PatientChartSection key={subGroup.id}>
                {props.section === PatientCaseGrouping.WORKUP ? (
                  <PatientChartWorkupPanels
                    qfs={subGroup.qfs}
                    gotoSection={handleGoToSection}
                    gotoDataInput={createSubGroupClickHandler(props.step)}
                    subGroupId={subGroup.id}
                    section={props.section}
                    onRemove={remove}
                  />
                ) : (
                  <>
                    <PatientChartSubGroupTitle
                      subGroupId={subGroup.id}
                      gotoDataInput={createSubGroupClickHandler(props.step)}
                      section={props.section}
                      showCheck={
                        subGroup.id === KahunPanelSubGroup.VitalSign &&
                        isAllVitalSignsNormal
                      }
                      element={
                        subGroup.id === KahunPanelSubGroup.VitalSign ? (
                          <ShowAllText onClick={onShowAll}>
                            {showMore ? "Show Abnormal" : "Show All"}
                          </ShowAllText>
                        ) : (
                          <></>
                        )
                      }
                    />
                    <QfsList
                      qfs={subGroup.qfs ? subGroup.qfs : []}
                      remove={remove}
                      gotoSection={handleGoToSection}
                      onRefine={handleOnRefineClicked(subGroup.id)}
                      subGroupId={subGroup.id}
                    />
                  </>
                )}
              </PatientChartSection>
            )}
          </React.Fragment>
        ))}
      </GroupedFeatureList>
    </PatientChartGroupedFeaturesView>
  );
};
